"use client";

import { c as _c } from "react/compiler-runtime";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import { ImagingResultsTable } from "@/src/components/cards/imaging-results-card/imaging-results-table";
import { LabResultsTable } from "@/src/components/cards/lab-results-card/lab-results-table";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import { ProgressNoteTable } from "@/src/components/cards/progress-note-card/progress-note-table";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import ErrorAlert from "@/src/components/error-alert";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { useEnabledFields } from "@/src/lib/data-fetchers/schema-fetchers";
import { Classification, isImagingResultsClassification, isLabResultsClassification, isProgressNoteClassification } from "@/src/models/constants/classifications";
import { ImagingResultsDocumentType, LabResultsDocumentType, ProgressNoteDocumentType } from "@/src/models/constants/document-types";
import { Specialty } from "@/src/models/constants/specialty";
import { ImagingResults } from "@/src/models/documents/imaging/imaging-results";
import { LabResults } from "@/src/models/documents/lab-results/lab-results";
import { MedicalHistory } from "@/src/models/documents/medical-history/medical-history";
import { ProgressNote } from "@/src/models/documents/progress-note/progress-note";
import { Referral } from "@/src/models/documents/referral";
import { FileType, File } from "@/src/models/file";
import { Section } from "@/src/models/section";
import { Task } from "@/src/models/task";
function USRCReferralInstructionsContent(t0) {
  const $ = _c(101);
  const {
    referral,
    sections
  } = t0;
  const referringLocation = referral.referringLocation;
  const receivingLocation = referral.receivingLocation;
  const getLocationName = _temp;
  const isMedicalHistoryChartClassification = _temp2;
  let t1;
  if ($[0] !== sections) {
    t1 = () => sections?.filter(_temp3).map(_temp4).filter(_temp5) || [];
    $[0] = sections;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getNephrologyProgressNotes = t1;
  let t2;
  if ($[2] !== sections) {
    t2 = () => sections?.filter(section_1 => isMedicalHistoryChartClassification(section_1.classification)).map(_temp6).filter(_temp7) || [];
    $[2] = sections;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getNephrologyMedicalChart = t2;
  let T0;
  let referringPhysician;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== getNephrologyMedicalChart || $[5] !== getNephrologyProgressNotes || $[6] !== receivingLocation) {
    const nephrologyProgressNotes = getNephrologyProgressNotes();
    const firstProgressNote = nephrologyProgressNotes.length > 0 ? nephrologyProgressNotes[0] as ProgressNote : null;
    const nephrologyMedicalChart = getNephrologyMedicalChart();
    const firstMedicalChart = nephrologyMedicalChart.length > 0 ? nephrologyMedicalChart[0] as MedicalHistory : null;
    const getDiagnosis = () => {
      if (!firstProgressNote && !firstMedicalChart) {
        return "N/A";
      }
      const diagnoses = firstProgressNote?.diagnosisList ?? firstMedicalChart?.diagnosisList;
      if (!diagnoses || diagnoses.length === 0) {
        return "N/A";
      }
      return diagnoses.map(_temp8).join("; ");
    };
    const getReferringPhysician = () => {
      if (!firstProgressNote && !firstMedicalChart) {
        return {
          name: "N/A",
          npi: "N/A"
        };
      }
      const provider = firstProgressNote?.provider || firstMedicalChart?.provider;
      if (!provider) {
        return {
          name: "N/A",
          npi: "N/A"
        };
      }
      const name = `${provider.givenName || ""} ${provider.familyName || ""}`.trim();
      const npi = provider.npiNumber || "";
      return {
        name: name || "N/A",
        npi: npi || "N/A"
      };
    };
    referringPhysician = getReferringPhysician();
    t14 = "flex flex-col gap-1";
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <span className="font-medium text-center">{"USRC"} Referral Instructions:</span>;
      $[22] = t15;
    } else {
      t15 = $[22];
    }
    t13 = "text-muted-foreground";
    t8 = "space-y-3 text-sm";
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <li className="flex items-start"><span className="mr-2">•</span><span>Verify that the patient information matches what's in their system</span></li>;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t16;
    let t17;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <span className="mr-2">•</span>;
      t17 = <strong>Check SSN:</strong>;
      $[24] = t16;
      $[25] = t17;
    } else {
      t16 = $[24];
      t17 = $[25];
    }
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <li className="flex items-start">{t16}<span>{t17} <CopyableContent text={"if blank, fill in with 4\u2019s"} /></span></li>;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    let t18;
    let t19;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <span className="mr-2">•</span>;
      t19 = <span><strong>Set Admit Type:</strong></span>;
      $[27] = t18;
      $[28] = t19;
    } else {
      t18 = $[27];
      t19 = $[28];
    }
    let t20;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <li>• If patient has NOT received outpatient HD before, patient is{" "}<CopyableContent text="NEW DIALYSIS PATIENT" /></li>;
      $[29] = t20;
    } else {
      t20 = $[29];
    }
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <li className="flex items-start">{t18}<div>{t19}<ul className="ml-4 mt-1 space-y-1">{t20}<li>• If patient has received outpatient HD before, patient is{" "}<CopyableContent text="TRANSFER FROM ANOTHER CENTER" /></li></ul></div></li>;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    let t21;
    let t22;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <span className="mr-2">•</span>;
      t22 = <strong>Set Intended facility (aka Receiving location):</strong>;
      $[31] = t21;
      $[32] = t22;
    } else {
      t21 = $[31];
      t22 = $[32];
    }
    let t23;
    if ($[33] !== receivingLocation) {
      t23 = getLocationName(receivingLocation);
      $[33] = receivingLocation;
      $[34] = t23;
    } else {
      t23 = $[34];
    }
    if ($[35] !== t23) {
      t12 = <li className="flex items-start">{t21}<span>{t22}{" "}<CopyableContent text={t23} /></span></li>;
      $[35] = t23;
      $[36] = t12;
    } else {
      t12 = $[36];
    }
    t6 = "flex items-start";
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <span className="mr-2">•</span>;
      t4 = <strong>Patient diagnosis:</strong>;
      $[37] = t4;
      $[38] = t7;
    } else {
      t4 = $[37];
      t7 = $[38];
    }
    t5 = " ";
    T0 = CopyableContent;
    t3 = getDiagnosis();
    $[4] = getNephrologyMedicalChart;
    $[5] = getNephrologyProgressNotes;
    $[6] = receivingLocation;
    $[7] = T0;
    $[8] = referringPhysician;
    $[9] = t10;
    $[10] = t11;
    $[11] = t12;
    $[12] = t13;
    $[13] = t14;
    $[14] = t15;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[7];
    referringPhysician = $[8];
    t10 = $[9];
    t11 = $[10];
    t12 = $[11];
    t13 = $[12];
    t14 = $[13];
    t15 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t16;
  if ($[39] !== T0 || $[40] !== t3) {
    t16 = <T0 text={t3} />;
    $[39] = T0;
    $[40] = t3;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t16 || $[43] !== t4 || $[44] !== t5) {
    t17 = <span>{t4}{t5}{t16}</span>;
    $[42] = t16;
    $[43] = t4;
    $[44] = t5;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <li>• CKD 4 and under is <CopyableContent text="AKI" /></li>;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ul className="ml-4 mt-1 space-y-1">{t18}<li>• CKD 5 is <CopyableContent text="ESRD" /></li></ul>;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t17) {
    t20 = <div>{t17}{t19}</div>;
    $[48] = t17;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== t20 || $[51] !== t6 || $[52] !== t7) {
    t21 = <li className={t6}>{t7}{t20}</li>;
    $[50] = t20;
    $[51] = t6;
    $[52] = t7;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  let t23;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span className="mr-2">•</span>;
    t23 = <span><strong>Patient modality:</strong></span>;
    $[54] = t22;
    $[55] = t23;
  } else {
    t22 = $[54];
    t23 = $[55];
  }
  let t24;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <li>• If diagnosis is AKI, then <CopyableContent text="AKI" /></li>;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <li className="flex items-start">{t22}<div>{t23}<ul className="ml-4 mt-1 space-y-1">{t24}<li>• If diagnosis is chronic, then <CopyableContent text="Incenter Hemodialysis" /></li><li className="text-xs text-muted-foreground">Examples of chronic dx: ESRD, Stage 4 kidney disease, Chronic kidney disease, etc.</li></ul></div></li>;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  let t27;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <span className="mr-2">•</span>;
    t27 = <span><strong>Intended start date:</strong></span>;
    $[58] = t26;
    $[59] = t27;
  } else {
    t26 = $[58];
    t27 = $[59];
  }
  let t28;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <li className="flex items-start">{t26}<div>{t27}<ul className="ml-4 mt-1 space-y-1"><li>• Should be 2 business days from now if it aligns with patient day preference</li><li>• If 2 business days lands on non-preferred day, choose the next day</li><li className="text-xs text-muted-foreground">Patient preference examples: MonWedFri, TueThuSat</li></ul></div></li>;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  let t30;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <span className="mr-2">•</span>;
    t30 = <strong>Referred by (aka Referring Location):</strong>;
    $[61] = t29;
    $[62] = t30;
  } else {
    t29 = $[61];
    t30 = $[62];
  }
  let t31;
  if ($[63] !== referringLocation) {
    t31 = getLocationName(referringLocation);
    $[63] = referringLocation;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== t31) {
    t32 = <li className="flex items-start">{t29}<span>{t30}{" "}<CopyableContent text={t31} /></span></li>;
    $[65] = t31;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  let t33;
  let t34;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <span className="mr-2">•</span>;
    t34 = <strong>Facility received referral:</strong>;
    $[67] = t33;
    $[68] = t34;
  } else {
    t33 = $[67];
    t34 = $[68];
  }
  let t35;
  if ($[69] !== receivingLocation) {
    t35 = getLocationName(receivingLocation);
    $[69] = receivingLocation;
    $[70] = t35;
  } else {
    t35 = $[70];
  }
  let t36;
  if ($[71] !== t35) {
    t36 = <li className="flex items-start">{t33}<span>{t34} <CopyableContent text={t35} /></span></li>;
    $[71] = t35;
    $[72] = t36;
  } else {
    t36 = $[72];
  }
  let t37;
  let t38;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <span className="mr-2">•</span>;
    t38 = <span><strong>Referring physician:</strong></span>;
    $[73] = t37;
    $[74] = t38;
  } else {
    t37 = $[73];
    t38 = $[74];
  }
  let t39;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <strong>Name:</strong>;
    $[75] = t39;
  } else {
    t39 = $[75];
  }
  let t40;
  if ($[76] !== referringPhysician.name) {
    t40 = <span className="text-sm">{t39} <CopyableContent text={referringPhysician.name} /></span>;
    $[76] = referringPhysician.name;
    $[77] = t40;
  } else {
    t40 = $[77];
  }
  let t41;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <strong>NPI:</strong>;
    $[78] = t41;
  } else {
    t41 = $[78];
  }
  let t42;
  if ($[79] !== referringPhysician.npi) {
    t42 = <span className="text-sm ml-4">{t41} <CopyableContent text={referringPhysician.npi} /></span>;
    $[79] = referringPhysician.npi;
    $[80] = t42;
  } else {
    t42 = $[80];
  }
  let t43;
  if ($[81] !== t40 || $[82] !== t42) {
    t43 = <li className="flex items-start">{t37}<div>{t38}<div className="mt-1">{t40}{t42}</div></div></li>;
    $[81] = t40;
    $[82] = t42;
    $[83] = t43;
  } else {
    t43 = $[83];
  }
  let t44;
  if ($[84] !== t10 || $[85] !== t11 || $[86] !== t12 || $[87] !== t21 || $[88] !== t32 || $[89] !== t36 || $[90] !== t43 || $[91] !== t8 || $[92] !== t9) {
    t44 = <ul className={t8}>{t9}{t10}{t11}{t12}{t21}{t25}{t28}{t32}{t36}{t43}</ul>;
    $[84] = t10;
    $[85] = t11;
    $[86] = t12;
    $[87] = t21;
    $[88] = t32;
    $[89] = t36;
    $[90] = t43;
    $[91] = t8;
    $[92] = t9;
    $[93] = t44;
  } else {
    t44 = $[93];
  }
  let t45;
  if ($[94] !== t13 || $[95] !== t44) {
    t45 = <span className={t13}>{t44}</span>;
    $[94] = t13;
    $[95] = t44;
    $[96] = t45;
  } else {
    t45 = $[96];
  }
  let t46;
  if ($[97] !== t14 || $[98] !== t15 || $[99] !== t45) {
    t46 = <div className={t14}>{t15}{t45}</div>;
    $[97] = t14;
    $[98] = t15;
    $[99] = t45;
    $[100] = t46;
  } else {
    t46 = $[100];
  }
  return t46;
}
function _temp8(diagnosis) {
  return diagnosis.diagnosisText;
}
function _temp7(medicalHistory) {
  return (medicalHistory as MedicalHistory).provider?.specialty?.includes(Specialty.NEPHROLOGY);
}
function _temp6(section_2) {
  return {
    ...(section_2.reviewedContent as MedicalHistory),
    classification: section_2.classification
  };
}
function _temp5(progressNote) {
  return (progressNote as ProgressNote).provider?.specialty?.includes(Specialty.NEPHROLOGY);
}
function _temp4(section_0) {
  return {
    ...(section_0.reviewedContent as ProgressNote),
    classification: section_0.classification
  };
}
function _temp3(section) {
  return isProgressNoteClassification(section.classification);
}
function _temp2(classification) {
  return classification === Classification.MEDICAL_HISTORY_CHART_FROM_CLINIC || classification === Classification.MEDICAL_HISTORY_CHART_FROM_HOSPITAL;
}
function _temp(location) {
  if (!location) {
    return "N/A";
  }
  return location.name || "N/A";
}
function USRCReferralCards(t0) {
  const $ = _c(33);
  const {
    referral,
    sections
  } = t0;
  const {
    data: enabledFields
  } = useEnabledFields();
  let t1;
  if ($[0] !== sections) {
    t1 = sections?.filter(_temp9).map(_temp10) || [];
    $[0] = sections;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const labResultsData = t1;
  let t2;
  if ($[2] !== sections) {
    t2 = sections?.filter(_temp11).map(_temp12).filter(_temp13) || [];
    $[2] = sections;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const progressNotesData = t2;
  let t3;
  if ($[4] !== sections) {
    t3 = sections?.filter(_temp14).map(_temp15) || [];
    $[4] = sections;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const imagingResultsData = t3;
  let t4;
  if ($[6] !== referral.patient) {
    t4 = <PatientCard patient={referral.patient} />;
    $[6] = referral.patient;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  let t6;
  if ($[8] !== referral) {
    t5 = <ReferralProviderCards referral={referral} />;
    t6 = <ReferralCard referral={referral} isEditable={true} />;
    $[8] = referral;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[11] !== referral.primaryCoverage) {
    t7 = referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable={true} />;
    $[11] = referral.primaryCoverage;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== referral.secondaryCoverage) {
    t8 = referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable={true} />;
    $[13] = referral.secondaryCoverage;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== enabledFields || $[16] !== progressNotesData) {
    t9 = progressNotesData.length > 0 && <ProgressNoteTable progressNotes={progressNotesData} enabledFields={enabledFields ? enabledFields[ProgressNoteDocumentType.PROGRESS_NOTE] : null} />;
    $[15] = enabledFields;
    $[16] = progressNotesData;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== enabledFields || $[19] !== labResultsData) {
    t10 = labResultsData.length > 0 && <LabResultsTable labResults={labResultsData} enabledFields={enabledFields ? enabledFields[LabResultsDocumentType.LAB_RESULTS] : null} />;
    $[18] = enabledFields;
    $[19] = labResultsData;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== enabledFields || $[22] !== imagingResultsData) {
    t11 = imagingResultsData.length > 0 && <ImagingResultsTable imagingResults={imagingResultsData} enabledFields={enabledFields ? enabledFields[ImagingResultsDocumentType.IMAGING_RESULTS] : null} />;
    $[21] = enabledFields;
    $[22] = imagingResultsData;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t4 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
    t12 = <>{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}</>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
}
function _temp15(section_4) {
  return {
    ...(section_4.reviewedContent as ImagingResults),
    classification: section_4.classification
  };
}
function _temp14(section_3) {
  return isImagingResultsClassification(section_3.classification);
}
function _temp13(progressNote) {
  return progressNote.classification === Classification.PROGRESS_NOTE_FLOW_SHEET || (progressNote as ProgressNote).provider?.specialty?.includes(Specialty.NEPHROLOGY);
}
function _temp12(section_2) {
  return {
    ...(section_2.reviewedContent as ProgressNote),
    classification: section_2.classification
  };
}
function _temp11(section_1) {
  return isProgressNoteClassification(section_1.classification);
}
function _temp10(section_0) {
  return {
    ...(section_0.reviewedContent as LabResults),
    classification: section_0.classification
  };
}
function _temp9(section) {
  return isLabResultsClassification(section.classification);
}
interface USRCReferralInstructionsProps {
  referral: Referral;
  task: Task;
}
export function USRCReferralInstructions(t0) {
  const $ = _c(8);
  const {
    referral,
    task
  } = t0;
  let t1;
  if ($[0] !== task.files) {
    t1 = task.files?.find(_temp16);
    $[0] = task.files;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const inboundFile = t1;
  let t2;
  if ($[2] !== inboundFile?.sections) {
    t2 = inboundFile?.sections || [];
    $[2] = inboundFile?.sections;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const sections = t2;
  if (!inboundFile) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <ErrorAlert message="No inbound file found for USRC referral instructions" />;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t3;
  if ($[5] !== referral || $[6] !== sections) {
    t3 = <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto"><EhrInstructionsCard><div className="text-sm text-muted-foreground"><USRCReferralInstructionsContent referral={referral} sections={sections} /></div></EhrInstructionsCard><USRCReferralCards referral={referral} sections={sections} /></div>;
    $[5] = referral;
    $[6] = sections;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp16(file) {
  return file.type === FileType.INBOUND;
}